import _ from 'lodash';
import {
  Prime100CoverageGroups,
  Prime100Coverages,
} from '../workflows/quotes/prime-100/types';
import { getCoverageNameForVersion } from '../workflows/quotes/prime-100/utils/coverages';
import { BoostRaterVersionResponse } from '../workflows/quotes/prime-100/api/types';

/**
 * V6-specific coverage options for P100 read-only view.
 * Uses different coverage structure than V5 (no Social Engineering, different first/third party coverages).
 */
const buildEndorsementsV6 = (
  params: any,
  opts: {
    onChange: any;
    register: any;
    coverageMapping: any;
    getLabel: (id: Prime100Coverages, name: string) => string | undefined;
  }
) => {
  const {
    hardwareReplCostEndorsement,
    hardwareReplCostSubLimit,
    postBreachRemediationEndorsement,
    postBreachRemediationSubLimit,
    serviceFraudAndCryptojackingSubLimit,
    dedicatedBreachCostsEndorsementSubLimit,
    telecomsFraudEndorsement,
    telecomsFraudSubLimit,
    blanketAdditionalInsuredEndorsement,
    additionalInsureds,
    additionalDefenseExpensesLimitEndorsement,
    additionalDefenseExpensesLimit,
    limit,
  } = params;
  const { onChange, register, coverageMapping, getLabel } = opts;

  const entries: any[] = [];
  let e: any;

  e = {};
  e.id = Prime100Coverages.HARDWARE_REPLACEMENT_COSTS;
  e.name = 'hardwareReplCostEndorsement';
  e.label = getLabel(Prime100Coverages.HARDWARE_REPLACEMENT_COSTS, coverageMapping?.HARDWARE_REPLACEMENT_COSTS?.name ?? '');
  e.defaultChecked = hardwareReplCostEndorsement;
  e.percentage = !hardwareReplCostEndorsement ? 0 : hardwareReplCostSubLimit / limit;
  e.onChange = onChange;
  e.register = register;
  e.disabled = !_.isEmpty(coverageMapping);
  e.checked = hardwareReplCostEndorsement;
  e.tooltip = !_.isEmpty(coverageMapping) && coverageMapping.HARDWARE_REPLACEMENT_COSTS?.tooltip;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.POST_BREACH_REMEDIATION;
  e.name = 'postBreachRemediationEndorsement';
  e.label = getLabel(Prime100Coverages.POST_BREACH_REMEDIATION, coverageMapping?.POST_BREACH_REMEDIATION?.name ?? '');
  e.defaultChecked = postBreachRemediationEndorsement;
  e.percentage = !postBreachRemediationEndorsement ? 0 : postBreachRemediationSubLimit / limit;
  e.onChange = onChange;
  e.register = register;
  e.disabled = !_.isEmpty(coverageMapping);
  e.checked = postBreachRemediationEndorsement;
  e.tooltip = !_.isEmpty(coverageMapping) && coverageMapping.POST_BREACH_REMEDIATION?.tooltip;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.SERVICE_FRAUD_AND_CRYPTOJACKING;
  e.name = 'serviceFraudAndCryptojackingSubLimit';
  e.label = !_.isEmpty(coverageMapping) && coverageMapping.SERVICE_FRAUD_AND_CRYPTOJACKING?.name;
  e.checked = !!serviceFraudAndCryptojackingSubLimit;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  e.percentage = serviceFraudAndCryptojackingSubLimit / limit;
  e.tooltip = !_.isEmpty(coverageMapping) && coverageMapping.SERVICE_FRAUD_AND_CRYPTOJACKING?.tooltip;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT;
  e.name = 'dedicatedBreachCostsEndorsementSubLimit';
  e.label = !_.isEmpty(coverageMapping) && coverageMapping.DEDICATED_BREACH_COSTS_ENDORSEMENT?.name;
  e.checked = !!dedicatedBreachCostsEndorsementSubLimit;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  e.percentage = dedicatedBreachCostsEndorsementSubLimit / limit;
  e.tooltip = !_.isEmpty(coverageMapping) && coverageMapping.DEDICATED_BREACH_COSTS_ENDORSEMENT?.tooltip;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.TELECOM_FRAUD;
  e.name = 'telecomsFraudEndorsement';
  e.label = getLabel(Prime100Coverages.TELECOM_FRAUD, coverageMapping?.TELECOM_FRAUD?.name ?? '');
  e.defaultChecked = telecomsFraudEndorsement;
  e.percentage = !telecomsFraudEndorsement ? 0 : telecomsFraudSubLimit / limit;
  e.onChange = onChange;
  e.register = register;
  e.disabled = !_.isEmpty(coverageMapping);
  e.checked = telecomsFraudEndorsement;
  e.tooltip = !_.isEmpty(coverageMapping) && coverageMapping.TELECOM_FRAUD?.tooltip;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.BLANKET_ADDITIONAL_INSURED;
  e.name = 'blanketAdditionalInsuredEndorsement';
  e.label = getLabel(Prime100Coverages.BLANKET_ADDITIONAL_INSURED, coverageMapping?.BLANKET_ADDITIONAL_INSURED?.name ?? '');
  e.register = register;
  e.onChange = onChange;
  e.checked = !!blanketAdditionalInsuredEndorsement;
  e.disabled = false;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.ADDITIONAL_INSURED;
  e.name = 'additionalInsureds';
  e.label = !_.isEmpty(coverageMapping) && coverageMapping.ADDITIONAL_INSURED?.name;
  e.register = register;
  e.onChange = onChange;
  e.checked = additionalInsureds;
  e.disabled = false;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  entries.push(e);

  e = {};
  e.id = Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT;
  e.name = 'additionalDefenseExpensesLimitEndorsement';
  e.label = !_.isEmpty(coverageMapping) && coverageMapping.ADDITIONAL_DEFENSE_EXPENSES_LIMIT?.name;
  e.checked = !!additionalDefenseExpensesLimitEndorsement;
  e.percentage = additionalDefenseExpensesLimitEndorsement ? (additionalDefenseExpensesLimit ?? 0) / limit : 0;
  e.mandatory = false;
  e.groupName = Prime100CoverageGroups.ENDORSEMENTS;
  e.tooltip = !_.isEmpty(coverageMapping) && coverageMapping.ADDITIONAL_DEFENSE_EXPENSES_LIMIT?.tooltip;
  entries.push(e);

  return entries;
};

export const calculateCoverageOptionsV6 = (
  {
    ransomPaymentLimit,
    extortionThreatsAndRansomPayment,
    hardwareReplCostEndorsement,
    hardwareReplCostSubLimit,
    telecomsFraudEndorsement,
    telecomsFraudSubLimit,
    postBreachRemediationEndorsement,
    postBreachRemediationSubLimit,
    websiteMediaContentLiabilityEndorsement,
    websiteMediaContentLiabilitySubLimit,
    limit,
    additionalInsureds,
    blanketAdditionalInsuredEndorsement,
    isTriaExcluded,
    contingentBusinessIncomeSecurityFailureEndorsement,
    contingentBusinessIncomeSystemFailureItEndorsement,
    dedicatedBreachCostsEndorsementSubLimit,
    serviceFraudAndCryptojackingSubLimit,
    fundsTransferFraudAndSocialEngineeringLimit,
    fundsTransferFraudAndSocialEngineeringEndorsement,
    fundsTransferFraudAndSocialEngineeringSubLimit,
    fundsTransferFraudAndSeWithConditionPrecedentLimit,
    fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement,
    fundsTransferFraudAndSocialEngineeringWithConditionPrecedentSubLimit,
    fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement,
    invoiceManipulationEndorsement,
    invoiceManipulationSubLimit,
    additionalDefenseExpensesLimitEndorsement,
    additionalDefenseExpensesLimit,
    businessIncomeAndExtraExpenseSubLimit,
    fundsTransferFraudCoverageLimit,
    socialEngineeringFraudCoverageLimit,
    reputationalHarmSubLimit,
    fundsTransferLiabilitySubLimit,
    courtAttendanceCosts,
  }: any,
  {
    onChange = null,
    register,
    coverageMapping,
    state,
    boostVersion = BoostRaterVersionResponse.V6,
  }: any = {}
) => {
  const getLabel = (coverageId: Prime100Coverages, apiName: string) =>
    getCoverageNameForVersion(coverageId, boostVersion, apiName ?? '');

  return [
    {
      id: Prime100Coverages.SECURITY_BREACH_EXPENSE,
      label: getLabel(
        Prime100Coverages.SECURITY_BREACH_EXPENSE,
        coverageMapping?.SECURITY_BREACH_EXPENSE?.name ?? ''
      ),
      defaultChecked: true,
      percentage: 1,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.SECURITY_BREACH_EXPENSE?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA,
      label: getLabel(
        Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA,
        coverageMapping?.RESTORATION_OF_ELECTRONIC_DATA?.name ?? ''
      ),
      defaultChecked: true,
      percentage: 1,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.RESTORATION_OF_ELECTRONIC_DATA?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT,
      label: getLabel(
        Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT,
        coverageMapping?.REPUTATIONAL_HARM_SUBLIMIT?.name ?? ''
      ),
      defaultChecked: true,
      percentage: (reputationalHarmSubLimit ?? 0) / limit,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.REPUTATIONAL_HARM_SUBLIMIT?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT,
      label: getLabel(
        Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT,
        coverageMapping?.EXTORTION_THREATS_AND_RANSOM_PAYMENT?.name ?? ''
      ),
      defaultChecked: true,
      percentage: ransomPaymentLimit / limit,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.EXTORTION_THREATS_AND_RANSOM_PAYMENT?.tooltip,
      disabled: false,
      mandatory: true,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT,
      label: getLabel(
        Prime100Coverages.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT,
        coverageMapping?.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT?.name ?? ''
      ),
      defaultChecked: true,
      disabled: false,
      percentage: businessIncomeAndExtraExpenseSubLimit / limit,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: 'contingentBusinessIncomeSF',
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: 'contingentBusinessIncomeSFIT',
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
      name: 'fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement',
      label: getLabel(
        Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
        coverageMapping?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
          ?.name ?? ''
      ),
      checked:
        !!fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement,
      percentage: fundsTransferFraudCoverageLimit / limit,
      mandatory: false,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
          ?.tooltip,
    },
    {
      id: 'ftfSeSubValues',
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: 'ftfSeCpSubValues',
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: 'fundsTransferHeldFunds',
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
      name: 'fundsTransferFraudAndSocialEngineering',
      label: getLabel(
        Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
        coverageMapping?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.name ?? ''
      ),
      checked: !!fundsTransferFraudAndSocialEngineeringEndorsement,
      percentage: !fundsTransferFraudAndSocialEngineeringEndorsement
        ? 0
        : fundsTransferFraudCoverageLimit / limit,
      mandatory: false,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.tooltip,
    },
    {
      id: Prime100Coverages.INVOICE_MANIPULATION,
      name: 'invoiceManipulationEndorsement',
      label:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.INVOICE_MANIPULATION?.name,
      checked: !!invoiceManipulationEndorsement,
      percentage: (invoiceManipulationSubLimit ?? 0) / limit,
      mandatory: false,
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.INVOICE_MANIPULATION?.tooltip,
    },
    {
      id: 'invoiceManipulationDeductible',
      groupName: Prime100CoverageGroups.FIRST_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.PCI_FINES_AND_PENALTIES,
      label:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.PCI_FINES_AND_PENALTIES?.name,
      defaultChecked: true,
      percentage: 1,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.PCI_FINES_AND_PENALTIES?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.THIRD_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.REGULATOR_DEFENSE_AND_PENALTIES,
      label:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.REGULATOR_DEFENSE_AND_PENALTIES?.name,
      defaultChecked: true,
      percentage: 1,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.REGULATOR_DEFENSE_AND_PENALTIES?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.THIRD_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.COURT_ATTENDANCE_COSTS,
      label:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.COURT_ATTENDANCE_COSTS?.name,
      defaultChecked: true,
      percentage: (courtAttendanceCosts ?? 0) / limit,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.COURT_ATTENDANCE_COSTS?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.THIRD_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
      label: getLabel(
        Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
        coverageMapping?.FUNDS_TRANSFER_LIABILITY_SUBLIMIT?.name ?? ''
      ),
      defaultChecked: true,
      percentage: fundsTransferLiabilitySubLimit
        ? fundsTransferLiabilitySubLimit / limit
        : 1,
      disabled: false,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.FUNDS_TRANSFER_LIABILITY_SUBLIMIT?.tooltip,
      mandatory: true,
      groupName: Prime100CoverageGroups.THIRD_PARTY_COVERAGES,
    },
    {
      id: Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY_V6,
      name: 'websiteMediaContentLiabilityEndorsement',
      label:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.WEBSITE_MEDIA_CONTENT_LIABILITY_V6?.name,
      defaultChecked: websiteMediaContentLiabilityEndorsement,
      percentage: websiteMediaContentLiabilitySubLimit / limit,
      onChange,
      register,
      disabled: !_.isEmpty(coverageMapping),
      checked: websiteMediaContentLiabilityEndorsement,
      tooltip:
        !_.isEmpty(coverageMapping) &&
        coverageMapping.WEBSITE_MEDIA_CONTENT_LIABILITY_V6?.tooltip,
      mandatory: false,
      groupName: Prime100CoverageGroups.THIRD_PARTY_COVERAGES,
    },
    {
      id: 'websiteMediaContentLiabilityDeductible',
      groupName: Prime100CoverageGroups.THIRD_PARTY_COVERAGES,
    },
    ...buildEndorsementsV6(
      {
        hardwareReplCostEndorsement,
        hardwareReplCostSubLimit,
        postBreachRemediationEndorsement,
        postBreachRemediationSubLimit,
        serviceFraudAndCryptojackingSubLimit,
        dedicatedBreachCostsEndorsementSubLimit,
        telecomsFraudEndorsement,
        telecomsFraudSubLimit,
        blanketAdditionalInsuredEndorsement,
        additionalInsureds,
        additionalDefenseExpensesLimitEndorsement,
        additionalDefenseExpensesLimit,
        limit,
      },
      { onChange, register, coverageMapping, getLabel }
    ),
    {
      id: Prime100Coverages.TRIA,
      name: 'isTriaExcluded',
      label: !_.isEmpty(coverageMapping) && coverageMapping.TRIA?.name,
      checked: !isTriaExcluded,
      mandatory: false,
      groupName: Prime100CoverageGroups.TRIA,
    },
  ];
};

export const waitingPeriods = {
  6: '6 Hours',
  8: '8 Hours',
  12: '12 Hours',
  24: '24 Hours',
};

export const retroActivePeriods = {
  1: '1 Year',
  2: '2 Years',
  3: 'Full Prior Acts',
};
